import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import { useDocuments } from "hooks/useDocuments";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import NoDocuments from "Assets/Img/NoDocuments";
import Input from "Components/Common/Input/Input";
import Button from "Components/Common/Button/Button";

interface Props {
  isActive: boolean;
}

const CATEGORIES = [
  { value: "pre_construction", label: "Pre-Construction" },
  { value: "csc_report", label: "CSC Report" },
  { value: "draw_backup", label: "Draw Backup" },
  { value: "general", label: "Project Document" },
] as const;

const getIconLabel = (fileName: string) => {
  const ext = (fileName.split(".").pop() ?? "").toLowerCase();
  if (ext === "pdf") return "PDF";
  if (["doc", "docx"].includes(ext)) return "DOC";
  if (["xls", "xlsx", "csv"].includes(ext)) return "XLS";
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return "IMG";
  return "FILE";
};

const formatGroupKey = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function DocSkeleton() {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <div className="doc-item" key={i}>
          <Skeleton width={40} height={40} borderRadius={6} />
          <div className="doc-skel-mid">
            <Skeleton width={220} height={13} />
            <Skeleton width={160} height={11} className="doc-skel-meta" />
          </div>
          <Skeleton width={80} height={30} borderRadius={6} />
        </div>
      ))}
    </>
  );
}

export default function OwnerDocumentsSection({ isActive }: Props) {
  const { id: projectId } = useParams<{ id: string }>();
  const {
    documents,
    loading,
    uploading,
    downloadingId,
    handleDownload,
    handleUpload,
  } = useDocuments(projectId, isActive);

  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedCategory, setSelectedCategory] = useState("general");
  const [categoryError, setCategoryError] = useState(false);

  const entries = documents ? Object.entries(documents) : [];
  const totalCount = entries.reduce(
    (sum, [, docs]) => sum + (docs as any[]).length,
    0,
  );

  const handleUploadClick = () => {
    if (uploading) return;
    if (!selectedCategory) {
      setCategoryError(true);
      return;
    }
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file, selectedCategory);
    e.target.value = "";
    // setSelectedCategory("");
    setCategoryError(false);
  };

  return (
    <>
      <div className="section-title">Documents &amp; Files</div>
      <div className="section-sub">
        Project documents. Upload additional documents or download existing
        files.
      </div>

      <div className="card">
        <div className="card-title">Upload a Document</div>
        {/* for future */}
        {/* <div style={{ marginBottom: 10 }}>
          <Select
            value={selectedCategory}
            options={[
              { value: "", label: "Select document category *" },
              ...CATEGORIES,
            ]}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setCategoryError(false);
            }}
            className={"ssel"}
          />
          {categoryError && (
            <div
              style={{
                color: "var(--error, #e53935)",
                fontSize: 12,
                marginTop: 4,
              }}
            >
              Please select a category before uploading.
            </div>
          )}
        </div> */}

        <Input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
          className="hidden-input"
          onChange={handleChange}
        />
        <div
          className={`upload-area${uploading ? " uploading" : ""}`}
          onClick={handleUploadClick}
        >
          <div className="ua-icon">{uploading ? "⏳" : "📎"}</div>
          <div className="ua-text">
            {uploading ? "Uploading..." : "Click to upload a document"}
          </div>
          <div className="ua-sub">PDF, JPG, PNG, XLSX — up to 25MB</div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">
          Project Documents
          {totalCount > 0 && !loading ? ` · ${totalCount} files` : ""}
        </div>

        {loading ? (
          <DocSkeleton />
        ) : totalCount === 0 ? (
          <EmptyState
            icon={<NoDocuments />}
            title="No Documents Yet"
            subtitle="Project documents will appear here once they are uploaded and published."
          />
        ) : (
          entries.map(([groupKey, docs]) => (
            <div key={groupKey}>
              <div className="doc-section-title">
                {formatGroupKey(groupKey)}
              </div>
              {docs.map((doc) => {
                const uploaderName = [
                  doc.uploader?.firstName,
                  doc.uploader?.lastName,
                ]
                  .filter(Boolean)
                  .join(" ");
                const date = doc.addOns?.date
                  ? new Date(doc.addOns.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : new Date(doc.createdAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    });
                const meta = [uploaderName, doc.uploader?.companyName, date]
                  .filter(Boolean)
                  .join(" · ");

                return (
                  <div className="doc-item" key={doc.id}>
                    <div className="doc-icon">
                      {getIconLabel(doc.value?.fileName ?? "")}
                    </div>
                    <div>
                      <div className="doc-name">{doc.label}</div>
                      {meta && <div className="doc-meta">{meta}</div>}
                    </div>
                    <Button
                      className="doc-btn"
                      disabled={downloadingId === doc.id}
                      onClick={() => handleDownload(doc)}
                    >
                      {downloadingId === doc.id ? (
                        <>
                          <span className="btn-spinner" /> Downloading…
                        </>
                      ) : (
                        "Download"
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </>
  );
}
